import { GridColDef } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
  dateCellValueFormatter,
} from '~/components/SolaForceDataGrid';
import { useAllEmployees } from '~/data/employees';
import { WorkflowInternalPositionReportData } from '~/data/reports';
import { useReportNavigateToView } from '~/scenes/PortalModules/Analytics/common';
import { convertToDateOrUndefined } from '~/utils/helpers/date';

import {
  InternalPositionsReportRow,
  WorkflowReportHookOutput,
  WorkflowReportHookProps,
} from '../../types';
import { DefaultChangedCell } from '../components/ChangedCellsFormatter/DefaultCell';
import { StartEndDateChangedCell } from '../components/ChangedCellsFormatter/StartEndDate';
import { DateHistoryCell } from '../components/HistoryCellsFormatter/DateHistoryCell';
import { HistoryCell } from '../components/HistoryCellsFormatter/DefaultHistoryCell';
import { buildProposalValueChanges } from '../utils/buildProposalValueChanges';
import { mapWorkflowHistory } from '../utils/mapWorkflowHistory';

import { resolveInternalPositionTranslatedData } from './resolveInternalPositionTranslatedData';

export const useInternalPositionsReport = (
  props: WorkflowReportHookProps<WorkflowInternalPositionReportData>,
): WorkflowReportHookOutput<InternalPositionsReportRow> => {
  const { data, cellRenderState } = props;
  const { showChanges, showHistory } = cellRenderState;
  const { t } = useTranslation();
  const { goToEmployeeBasicDataView } = useReportNavigateToView();
  const { getEmployeeById } = useAllEmployees();

  const rows = useMemo(() => {
    return data.flatMap((dataEntry) => {
      const { fEmpId, fEmpDetails } = dataEntry;

      return dataEntry.fProposals.map(
        (proposal): InternalPositionsReportRow => {
          const { fProposal, fReferenceData, fWorkflowEntries } = proposal;

          const workflowHistory = mapWorkflowHistory(
            fWorkflowEntries,
            getEmployeeById,
          );

          const proposalData = resolveInternalPositionTranslatedData(
            fProposal.translate,
            getEmployeeById,
          );

          const referenceData = fReferenceData?.translate
            ? resolveInternalPositionTranslatedData(
                fReferenceData.translate,
                getEmployeeById,
              )
            : undefined;

          const changes = referenceData
            ? buildProposalValueChanges(referenceData, proposalData)
            : undefined;

          return {
            employeeId: fEmpId,
            firstName: fEmpDetails.fEmpFirstName,
            lastName: fEmpDetails.fEmpLastName,
            employeeNumber: fEmpDetails.fEmpPersonNumber,
            unit: fEmpDetails.fEmpUnitDetails.fOrgUnitName,
            legalEntity: fEmpDetails.fEmpLegalEntityName,
            costCenters: fEmpDetails.fEmpCostCenterDetails.fEmpCostCenterName,
            ...proposalData,
            startDate: convertToDateOrUndefined(proposalData.startDate),
            endDate: convertToDateOrUndefined(proposalData.endDate),
            workflowHistory,
            changes,
          };
        },
      );
    });
  }, [data, getEmployeeById]);

  const columns = useMemo(
    (): GridColDef<InternalPositionsReportRow>[] => [
      {
        field: 'employeeId',
        headerName: t('laPicture'),
        filterable: false,
        sortable: false,
        disableExport: true,
        minWidth: 30,
        renderCell: (params) => (
          <EmployeeCellComponent
            {...params}
            pictureOnly={true}
            onClick={() => goToEmployeeBasicDataView(params.row.employeeId)}
          />
        ),
      },
      {
        field: 'firstName',
        headerName: t('laFirstName'),
        hideable: false,
        minWidth: 90,
        renderCell: (params) => (
          <ButtonCellComponent
            {...params}
            onClick={() => goToEmployeeBasicDataView(params.row.employeeId)}
          />
        ),
      },
      {
        field: 'lastName',
        headerName: t('laLastName'),
        hideable: false,
        minWidth: 110,
        renderCell: (params) => (
          <ButtonCellComponent
            {...params}
            onClick={() => goToEmployeeBasicDataView(params.row.employeeId)}
          />
        ),
      },
      {
        field: 'employeeNumber',
        headerName: t('laEmpId'),
        minWidth: 130,
      },
      {
        field: 'position',
        headerName: t('laPosition'),
        minWidth: 150,
        renderCell: (params) =>
          DefaultChangedCell({
            params,
            showChanges,
          }),
      },
      {
        field: 'unit',
        headerName: t('laOrgUnit'),
        minWidth: 180,
        renderCell: (params) =>
          DefaultChangedCell({
            params,
            showChanges,
          }),
      },
      {
        field: 'manager',
        headerName: t('laManager'),
        minWidth: 180,
        renderCell: (params) =>
          DefaultChangedCell({
            params,
            showChanges,
          }),
      },
      {
        field: 'legalEntity',
        headerName: t('laLegalEntity'),
        minWidth: 180,
        renderCell: (params) =>
          DefaultChangedCell({
            params,
            showChanges,
          }),
      },
      {
        field: 'costCenters',
        headerName: t('laCostCenter'),
        minWidth: 180,
        renderCell: (params) =>
          DefaultChangedCell({
            params,
            showChanges,
          }),
      },
      {
        field: 'costCenterPercentage',
        headerName: t('laPercCostCenter'),
        minWidth: 180,
        renderCell: (params) =>
          DefaultChangedCell({
            params,
            showChanges,
          }),
      },
      {
        field: 'info',
        headerName: t('laInfo'),
        minWidth: 200,
        renderCell: (params) =>
          DefaultChangedCell({
            params,
            showChanges,
          }),
      },
      {
        field: 'startDate',
        headerName: t('laStartDate'),
        minWidth: 130,
        type: 'date',
        valueFormatter: dateCellValueFormatter,
        renderCell: (params) =>
          StartEndDateChangedCell({
            params,
            showChanges,
          }),
      },
      {
        field: 'endDate',
        headerName: t('laEndDate'),
        minWidth: 130,
        type: 'date',
        valueFormatter: dateCellValueFormatter,
        renderCell: (params) =>
          StartEndDateChangedCell({
            params,
            showChanges,
          }),
      },
      {
        field: 'jobTitle',
        headerName: t('laJobTitle'),
        minWidth: 180,
        renderCell: (params) =>
          DefaultChangedCell({
            params,
            showChanges,
          }),
      },
      {
        field: 'scheduledDate',
        headerName: t('laScheduledDate'),
        minWidth: 150,
        type: 'date',
        renderCell: (params) => (
          <DateHistoryCell
            workflowHistory={params.row.workflowHistory}
            showHistory={showHistory}
            fieldKey="scheduledDate"
          />
        ),
      },
      {
        field: 'updatedAt',
        headerName: t('Updated'),
        minWidth: 170,
        renderCell: (params) => (
          <DateHistoryCell
            workflowHistory={params.row.workflowHistory}
            showHistory={showHistory}
            fieldKey="updatedAt"
            format="DD/MM/YYYY HH:mm:ss"
          />
        ),
      },
      {
        field: 'status',
        headerName: t('laStatus'),
        minWidth: 150,
        renderCell: (params) => (
          <HistoryCell
            workflowHistory={params.row.workflowHistory}
            showHistory={showHistory}
            fieldKey="status"
          />
        ),
      },
      {
        field: 'approvedBy',
        headerName: t('laApprovedBy'),
        minWidth: 150,
        renderCell: (params) => (
          <HistoryCell
            workflowHistory={params.row.workflowHistory}
            showHistory={showHistory}
            fieldKey="approvedBy"
          />
        ),
      },
    ],
    [t, goToEmployeeBasicDataView, showHistory, showChanges],
  );

  return { rows, columns };
};
